import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface IAutoLogoffWarningProps {
  isOpen: boolean;
  onExtendSession: () => void;
  onLogout: () => void;
  minutesRemaining: number;
}

/**
 * Modal component that warns users about impending auto-logout
 * Shows a countdown timer and allows users to extend their session or logout immediately
 */
const AutoLogoffWarning = (props: IAutoLogoffWarningProps) => {
  const { isOpen, onExtendSession, onLogout, minutesRemaining } = props;
  const [timeLeft, setTimeLeft] = useState(minutesRemaining * 60); // Convert to seconds

  useEffect(() => {
    if (isOpen) {
      setTimeLeft(minutesRemaining * 60);
      
      const interval = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            onLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isOpen, minutesRemaining, onLogout]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} backdrop="static" keyboard={false} centered>
      <ModalHeader className="text-warning">
        <FontAwesomeIcon icon="exclamation-triangle" className="text-warning mr-2" />
        Session Timeout Warning
      </ModalHeader>
      <ModalBody>
        <div className="text-center">
          <p>Your session will expire in:</p>
          <h3 className="text-danger font-weight-bold">{formatTime(timeLeft)}</h3>
          <p className="mb-2">You will be automatically logged out due to inactivity.</p>
          <p className="text-muted">Click &ldquo;Extend Session&rdquo; to continue working.</p>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={onExtendSession} className="mr-2">
          <FontAwesomeIcon icon="clock" />
          &nbsp; Extend Session
        </Button>
        <Button color="secondary" onClick={onLogout}>
          <FontAwesomeIcon icon="sign-out-alt" />
          &nbsp; Logout Now
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AutoLogoffWarning;